import React from 'react';
import { Dropdown, Layout, Menu } from 'antd';
import { CrownOutlined, DownOutlined, GiftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../i18n';
import type { SellerAuthShop } from '../../../types/seller';
import { useSelectedShop } from './selectedShopContext';

const regionLabelKey: Record<string, string> = {
  VN: 'store.market_vn',
  TH: 'store.market_th',
  ID: 'store.market_id',
  MY: 'store.market_my',
  PH: 'store.market_ph',
  SG: 'store.market_sg'
};

export type MenuIconTone =
  | 'violet'
  | 'blue'
  | 'cyan'
  | 'indigo'
  | 'rose'
  | 'sky'
  | 'amber'
  | 'emerald';

export type AppMenuItem = {
  key: string;
  icon?: React.ReactNode;
  iconTone?: MenuIconTone;
  label: React.ReactNode;
  children?: AppMenuItem[];
};

function wrapMenuIcon(icon: React.ReactNode, tone: MenuIconTone) {
  return (
    <span className={`main-sider-menu-icon main-sider-menu-icon--${tone}`} aria-hidden>
      {icon}
    </span>
  );
}

type MainSiderProps = {
  collapsed: boolean;
  items: AppMenuItem[];
  selectKeys: string[];
  openKeys: string[];
  onMenuSelect: (key: string) => void;
  onOpenChange: (keys: string[]) => void;
};

export function MainSider({
  collapsed,
  items,
  selectKeys,
  openKeys,
  onMenuSelect,
  onOpenChange,
}: MainSiderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { shops, shopId, setShopId } = useSelectedShop();
  const [shopDropdownOpen, setShopDropdownOpen] = React.useState(false);
  const selectedShop = shops.find((shop) => shop.shopId === shopId);

  const regionLabel = (shop: SellerAuthShop) => {
    const code = (shop.shopRegion ?? '').trim().toUpperCase();
    const key = regionLabelKey[code];
    return key ? t(key) : code || '—';
  };

  const shopDropdownPanel = (
    <div className="main-sider-shop-panel">
      {shops.length === 0 ? (
        <div className="main-sider-shop-option-region px-3 py-2">{t('layout.no_shop')}</div>
      ) : (
        shops.map((shop) => (
          <button
            key={shop.shopId}
            type="button"
            className={`main-sider-shop-option${shopId === shop.shopId ? ' is-active' : ''}`}
            onClick={() => {
              if (shop.shopId) setShopId(shop.shopId);
              setShopDropdownOpen(false);
            }}
          >
            <span className="main-sider-shop-dot main-sider-shop-dot--green" />
            <span className="main-sider-shop-option-name">{shop.shopName?.trim() || '—'}</span>
            <span className="main-sider-shop-option-region">{regionLabel(shop)}</span>
          </button>
        ))
      )}
    </div>
  );

  const menuItems = React.useMemo(() => {
    const mapItem = (item: AppMenuItem): AppMenuItem => ({
      ...item,
      icon: item.icon != null ? wrapMenuIcon(item.icon, item.iconTone ?? 'violet') : undefined,
      children: item.children?.map(mapItem)
    });
    return items.map(mapItem);
  }, [items]);

  const { Sider } = Layout;

  return (
    <Sider
      className="main-sider"
      trigger={null}
      collapsible
      collapsed={collapsed}
      theme="light"
    >

      <Menu
        className="main-sider-menu"
        style={{ flex: 1, minHeight: 0, borderInlineEnd: 'none', paddingTop: '6px' }}
        selectedKeys={selectKeys}
        openKeys={openKeys}
        items={menuItems}
        mode="inline"
        onSelect={(info) => onMenuSelect(info.key)}
        onOpenChange={onOpenChange}
      />
      <div className="main-sider-purchase p-2">
        <button
          type="button"
          className={`main-sider-purchase-btn${pathname === '/creator/upgrade' ? ' is-active' : ''}`}
          title={t('layout.buy_plan')}
          onClick={() => navigate('/creator/upgrade')}
        >
          {collapsed ? <CrownOutlined /> : t('layout.buy_plan')}
        </button>
        <button
          type="button"
          className={`main-sider-purchase-btn main-sider-purchase-btn--entitlement${pathname === '/creator/entitlements' ? ' is-active' : ''}`}
          title={t('layout.buy_entitlement')}
          onClick={() => navigate('/creator/entitlements')}
        >
          {collapsed ? <GiftOutlined /> : t('layout.buy_entitlement')}
        </button>
      </div>
      <div className="main-sider-shop">

        <Dropdown
          open={shopDropdownOpen}
          onOpenChange={setShopDropdownOpen}
          trigger={['click']}
          placement="topLeft"
          arrow={false}
          dropdownRender={() => shopDropdownPanel}
          overlayClassName="main-sider-shop-overlay"
          getPopupContainer={(node) =>
            node.closest('.main-sider') ?? document.body
          }
        >
          <button
            type="button"
            className="main-sider-shop-trigger"
            aria-expanded={shopDropdownOpen}
            aria-haspopup="listbox"
          >
            <span className="main-sider-shop-dot main-sider-shop-dot--green" />
            {!collapsed && (
              <>
                <span className="main-sider-shop-trigger-text">
                  <span className="main-sider-shop-trigger-name">
                    {selectedShop?.shopName?.trim() || t('layout.no_shop')}
                  </span>
                  {selectedShop ? (
                    <span className="main-sider-shop-trigger-meta">{regionLabel(selectedShop)}</span>
                  ) : null}
                </span>
                <DownOutlined className="main-sider-shop-trigger-chevron" />
              </>
            )}
          </button>
        </Dropdown>
      </div>
    </Sider>
  );
}
